/**
 * ticket-inapp.stencil-wordmark — the display wordmark the `stencil` in-app
 * card variant's identity rests on (ticket-inapp.stencil.template.ts) and the
 * wallet band reproduces (ticket-wallet.panel.ts): the name set in the card's
 * own condensed display face, uppercased, at zero tracking, and scaled to span
 * exactly the box it is given. The treatment is purely mechanical — no
 * per-letter judgment and no hardcoded glyph — so it renders correctly for ANY
 * event name and ANY operator brand.
 *
 * NO CALLER OF `<tkt-fitted-wordmark>` CAN CLIP, FOR ANY STRING — the hero
 * title uses `mode="fill"` (always scales to span its box exactly, for
 * ANY length, the "spans edge to edge" look) and the footer brand uses
 * `mode="cap"` (scales DOWN only when its natural content would overflow,
 * never scales up a short brand name to fill the whole footer). Both modes
 * measure the SAME way — the element's own rendered box against its
 * content's natural rendered box — so a single 40+ character unbroken token
 * (no internal space to wrap at) is exactly as safe as a short one: the
 * correction wraps the WHOLE measured run, never a per-character or
 * per-word one, so it has no dependency on where — or whether — the string
 * could otherwise break.
 *
 * THIS TREATMENT IS NOT PART OF EDITORIAL OR STRATA — those two compositions
 * set their titles as ordinary type. `renderWordmark` below is drawn only by
 * the stencil card's hero and footer and by the wallet band that reproduces
 * that hero, which is why a change to it moves one design and not three.
 *
 * WHY A CUSTOM ELEMENT, NOT AN SVG `textLength` RENDER: SVG's `textLength` +
 * `lengthAdjust="spacingAndGlyphs"` forces a `<text>` run to an exact width by
 * compressing glyph advances, which is the right layout primitive for the
 * width-fitting job alone — but it cannot carry the per-glyph flip
 * `WORDMARK_FLIPS_GLYPHS` below switches on. Verified against the real
 * Chromium path this package's file render uses: a per-`<tspan>` CSS
 * `transform: scaleX(-1)` computes correctly in the CSSOM
 * (`getComputedStyle` reports the matrix) yet does not visibly paint, because
 * Chromium's glyph-run painter for a `spacingAndGlyphs`-adjusted `<text>` does
 * not respect a descendant `<tspan>`'s own transform; a per-tspan `transform`
 * presentation attribute with hand-computed positions instead abandons
 * `textLength` altogether (a manually `x`-positioned run has no width-forcing
 * primitive of its own) and needs an equal-width character slot narrower than
 * several of this package's own glyphs, so characters overlap.
 * `<tkt-fitted-wordmark>` below keeps the DOM-span run and solves ONLY the
 * width-fitting job, with one ordinary CSS transform on a container. The
 * measurement is a pure function of its own (deterministic) inputs — same
 * text, font, and target box always measure the same natural size and land the
 * same scale factor — so the render stays deterministic across repeats, the
 * same invariant `ticket.props.ts` states for every value on this card.
 *
 * SCALE-INVARIANT ACROSS EVERY RENDER SIZE — the in-app card, the PDF/file
 * render, and every scaled/thumbnail rendering (`renderTicketCardScaled` —
 * the catalog's 280px, the picker's 120px) all mount the identical markup
 * this file returns, inside an ancestor CSS `transform: scale()` frame
 * (ticket-inapp.scaled-card.ts). This element's own fit measurement is
 * therefore taken entirely in the SAME (post-transform) screen space on
 * both sides of its ratio — never against a fixed, unscaled design-space
 * constant — so the correction holds at every one of those widths without
 * the element needing to know which one is currently active.
 */
import { html, LitElement, type TemplateResult } from "lit";
import {
	ensureTicketCardFonts,
	TICKET_CARD_DISPLAY_FAMILY,
	ticketCardFontSet,
} from "./ticket-inapp.fonts.ts";

/**
 * THE ONE SWITCH FOR THE ALTERNATING GLYPH FLIP, AND WHY IT IS OFF.
 *
 * A flip is selected by position, so it has no way to know whether the glyph
 * it reflects is still the letter: `L` mirrors into a shape a reader takes for
 * `J` and `E` into `Ǝ`, so `Hallen 07` renders as `HAJLƎN 07` — a misspelling,
 * not a mark. Only `A H I M O T U V W X Y 0 8` are near enough to symmetric to
 * survive a reflection, and restricting the flip to those is invisible by
 * definition, so no threshold, density, or per-letter table makes the
 * generated form both flipped and legible.
 *
 * The flipped identity belongs to a DESIGNED asset, where a human chose which
 * letters to reflect and shaped them so the word still reads: the operator
 * uploads it, and the card prefers it in both slots (`event.logoUrl` in the
 * hero, `brand.logoUrl` in the footer — ticket.props.ts). What this file draws
 * is the fallback for an operator who has uploaded nothing, and a fallback's
 * first duty is to be correct — a legible name beats an imitation of someone
 * else's logo.
 *
 * Setting this to `true` restores the alternating flip for every name and
 * every slot; nothing else moves, because the per-character boxes and the
 * `.tkt-wordmark-char--flip` rule (ticket-inapp.styles.ts) that carry it are
 * what the run is already built out of, and `WORDMARK_TRACKING_EM` below is
 * already the value a flipped run needs.
 */
const WORDMARK_FLIPS_GLYPHS: boolean = false;

/** DOM-span wordmark run, sized purely by the caller's own font-size —
 *  `<tkt-fitted-wordmark>`'s own raw material, never mounted un-fitted on
 *  this card (see that element's `mode="cap"` for a slot that must shrink
 *  but never stretch).
 *
 * WORDS, NOT THE WHOLE STRING, ARE THE inline-block UNIT: every character —
 * including the space — needs its own box for a transform to apply to it
 * (`transform` has no effect on a plain inline box), but a browser's normal
 * line-wrap only ever breaks at a genuine whitespace TEXT node. Trapping the
 * space character inside a span like every other glyph removes that break
 * opportunity entirely, so a long name runs off the card as a single
 * unbreakable line instead of wrapping. Each WORD is therefore its own
 * `whitespace-nowrap` box of character spans, joined by a literal space text
 * node the browser can wrap at; the flip index runs continuously across word
 * boundaries, so only where the line may break changes. This wrap opportunity
 * is moot once a caller mounts it through `<tkt-fitted-wordmark>`, whose own
 * wrapper forces `white-space: nowrap` on the whole run so the fit measurement
 * reads one single-line natural width — the SCALE correction is what keeps a
 * long or unbroken string inside its box, not a browser line-break. */
export function renderWordmark(text: string): TemplateResult {
	let index = 0;
	const words = text.toUpperCase().split(" ");
	return html`${words.map(
		(word, wi) => html`${wi > 0 ? " " : ""}<span class="tkt-wordmark-word"
			>${[...word].map((ch) => {
				const flipped = WORDMARK_FLIPS_GLYPHS && index % 2 === 0;
				index += 1;
				return flipped
					? html`<span class="tkt-wordmark-char--flip">${ch}</span>`
					: html`<span class="tkt-wordmark-char">${ch}</span>`;
			})}</span
		>`,
	)}`;
}

const FITTED_WORDMARK_TAG = "tkt-fitted-wordmark";

/**
 * THE ONE TRACKING EVERY WORDMARK SETS, AND WHY IT IS ZERO.
 *
 * A run that may flip glyphs (`WORDMARK_FLIPS_GLYPHS` above) cannot carry
 * negative tracking. Each character sits in its own inline-block whose width is
 * the glyph's advance PLUS the tracking, and the boxes are flush, so a negative
 * value narrows the boxes rather than opening space between them; a flip then
 * reflects the glyph about a box the tracking has already shortened. At every
 * boundary where an upright character meets a flipped one the reflection
 * re-anchors the glyph to the edge the tracking just pulled in, and the two
 * inks lose close to TWICE the tracking. Which pairs collide is then a property
 * of the string's own flip pattern rather than of the design — the same
 * tracking that reads as density on one name fuses the letters of the next.
 *
 * At zero, a flip can only ever exchange one of a glyph's side bearings for
 * the other, so every gap is a sum of two non-negative bearings and no string
 * in any face can collide. That is a proof rather than a tuning, which is what
 * a treatment applied mechanically to arbitrary operator and event names needs,
 * and it is what lets the flip switch be a one-line change.
 *
 * It is declared HERE, on the span that sets the wordmark's own font-size,
 * rather than left to the display class the card sets elsewhere
 * (`.tkt-card-display`, ticket-inapp.styles.ts). That class states its tracking
 * in em against its own 16px box, so a wordmark reading through it takes the
 * same absolute length whatever size it is set at — one value that reads as a
 * light touch against a 104px hero glyph and as a collision against a 17px
 * footer one. Set here, in em of the span's own size, it is size-relative by
 * construction.
 *
 * The hero's approved geometry does not move with it: `mode="fill"` re-fits the
 * run to exactly its box, so a tracking change is absorbed by the fit rather
 * than by the wordmark's width or its right edge.
 */
const WORDMARK_TRACKING_EM = 0;

/** `"fill"` always scales to span the box exactly, growing OR shrinking —
 *  the hero title's job, the reference's own "extends left to right" look,
 *  true for a one-character name exactly as it is for a 60-character one.
 *  `"cap"` only ever shrinks a natural render that would overflow the box;
 *  a short string stays at its own natural size — the footer brand's job,
 *  which reads at roughly a third of the hero's own size and must never
 *  stretch a short brand name up to fill a box sized for a long one. */
export type FittedWordmarkMode = "fill" | "cap";

/**
 * The custom element both `renderWordmarkFullWidth` (hero, `mode="fill"`) and
 * `renderWordmarkFooter` (brand, `mode="cap"`) mount: renders
 * `renderWordmark`'s output at `fontSizePx`, then measures that content's own
 * natural rendered width and applies one `transform: scaleX()` on its wrapper
 * so the whole run fits its box — the correction wraps the finished run rather
 * than touching any one glyph. `createRenderRoot`
 * returns `this` (light DOM, no shadow root): the glyph spans resolve their
 * rules against the card's own co-located stylesheet (ticket-inapp.styles.ts),
 * which the card emits into the mounting document and a shadow root would not
 * see without separately adopting it, and this element's whole job is narrow
 * enough that the isolation a shadow root buys is not worth that second
 * stylesheet wire-up.
 *
 * THE MEASUREMENT WAITS ON THE REAL, LOADED ANTON FONT FACE, NOT JUST LIT'S
 * OWN UPDATE CYCLE, AND NOT JUST `document.fonts.ready` — caught by a real
 * render, not assumed: `document.fonts.ready` resolves once every font the
 * browser has already started loading finishes, but a font is only added to
 * that loading set once the browser's own style/layout pass DISCOVERS text
 * that needs it — a known timing gap, not a guarantee that Anton specifically
 * has been discovered by the time this element's own update cycle checks
 * `.ready`, and a `.ready` accessed before that discovery resolves
 * immediately, leaving the FIRST measurement reading the fallback font's
 * (narrower or wider) natural width and clipping or under-filling once
 * Anton itself paints a moment later. `document.fonts.load(...)` for the
 * exact family this element renders sidesteps discovery entirely — it
 * explicitly requests the face and its returned promise resolves only once
 * that specific load settles. `getUpdateComplete` is Lit's own supported
 * extension point for chaining that extra async wait into `updateComplete`
 * — which is what the ticket-file render pipeline's own readiness contract
 * (`Promise.all` over every mounted element's `updateComplete`,
 * ticket-file.boot.ts) then genuinely waits on, so pdf-render's screenshot
 * (and, live, every scaled/thumbnail rendering) never fires on a
 * pre-font-load measurement.
 */
class TktFittedWordmark extends LitElement {
	static override properties = {
		text: { type: String },
		fontSizePx: { type: Number, attribute: "font-size-px" },
		mode: { type: String },
	};

	declare text: string;
	declare fontSizePx: number;
	declare mode: FittedWordmarkMode;

	constructor() {
		super();
		this.text = "";
		this.fontSizePx = 0;
		this.mode = "fill";
	}

	override createRenderRoot(): this {
		return this;
	}

	override connectedCallback(): void {
		super.connectedCallback();
		// The display face this wordmark is drawn in, registered on the
		// document by the card itself rather than by whatever host mounted it
		// — see ticket-inapp.fonts.ts for why a host-supplied font is a
		// dependency the card cannot rely on. Idempotent, so calling it from
		// every wordmark's own connect costs one lookup after the first.
		ensureTicketCardFonts();
		// Fills the wrapping box renderWordmarkFullWidth /
		// renderWordmarkFooter size below — this element's OWN
		// rendered width is the fit target #applyFit reads, so it must
		// actually span that box rather than shrink-wrap its (pre-fit) inline
		// content. The wrapper is a flex container (align-items: center, for
		// vertical centering), and a flex ITEM's main-axis (width) size
		// defaults to its own content, not the container's — `display: block`
		// alone does not stretch it; `width: 100%` is what actually spans the
		// box, in both modes: `"cap"` still needs a real target to measure
		// against, it just may choose not to fill it. The centring is the
		// state an uncorrected run sits at — `#applyFit` re-decides it against
		// the ratio it lands, for the reason stated there.
		this.style.display = "block";
		this.style.width = "100%";
		this.style.textAlign = "center";
		// This host itself renders no text of its own — only the span below
		// does, at its own explicit `line-height: 0.8` — but as a block box
		// wrapping inline content, it still generates its own "strut" (an
		// invisible zero-width inline box at the START of its line, using
		// THIS element's own inherited font-size/line-height, not the span's)
		// that contributes to the line box's total height alongside the
		// span's own. Caught by a real render, not assumed: the footer brand
		// wordmark's own rendered height measured ~24px against a span whose
		// OWN rendered height was ~13.6px — a ~10px strut contribution from
		// this host's ambient inherited metrics, tall enough that the
		// footer's box budget (`renderWordmarkFooter`'s `heightPx`,
		// sized off the span's own `fontSizePx`) didn't leave room for it,
		// clipping the glyph's own top/bottom against the wrapper's
		// `overflow: hidden`. `line-height: 0` here zeroes this host's own
		// strut to nothing, so the line box's height comes from the span's
		// real rendered content alone — the only measurement `#applyFit` and
		// every caller's own box budget already accounts for.
		this.style.lineHeight = "0";
		// A defensive second correction, alongside the awaited
		// document.fonts.load() below — belt and braces against any font
		// fetch this element's own await raced (a real network/file fetch in
		// every live-browser host, not the PDF path's already-inline data
		// URI): the FontFaceSet's own "loadingdone" fires once more whenever
		// ANY font on the page finishes loading, Anton included, so a
		// mis-measured first fit still self-corrects the moment the real
		// face is actually usable.
		// Absent in a host with no Font Loading API — the card still mounts
		// and still corrects, just without this extra re-run (see
		// ticket-inapp.fonts.ts's own `ticketCardFontSet`).
		ticketCardFontSet()?.addEventListener(
			"loadingdone",
			this.#onFontsLoadingDone,
		);
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		ticketCardFontSet()?.removeEventListener(
			"loadingdone",
			this.#onFontsLoadingDone,
		);
	}

	#onFontsLoadingDone = (): void => {
		this.#applyFit();
	};

	override render(): TemplateResult {
		return html`<span
      class="tkt-wordmark-word"
      style="transform-origin: left center; font-size: ${this.fontSizePx}px; line-height: 0.8; overflow-wrap: normal; letter-spacing: ${WORDMARK_TRACKING_EM}em;"
      >${renderWordmark(this.text)}</span
    >`;
	}

	/** Both measurements below are taken in screen space — this element's own
	 *  rendered width (the target, sized by its CSS-pixel parent box) and the
	 *  run span's natural rendered width (transform temporarily cleared)
	 *  — so an ancestor `transform: scale()` (any `renderTicketCardScaled`
	 *  target width) scales both sides of the ratio identically and cancels
	 *  out of it; comparing a scaled screen measurement against a fixed,
	 *  unscaled design-space constant is what would make the correction
	 *  wrong at any render size other than the 384px design width.
	 *
	 *  `mode="cap"` clamps the ratio at 1 — the one-line difference between
	 *  "always spans the box" and "never overflows it, never stretches to
	 *  fill it" — so a short brand name keeps its own natural size and a
	 *  long or unbroken one still shrinks to the box exactly like the hero
	 *  does. */
	#applyFit(): void {
		const span = this.firstElementChild as HTMLElement | null;
		// data-fit-gen increments on every #applyFit call regardless of
		// outcome — an unambiguous "the correction ran" signal a caller can
		// poll for (ticket-inapp.stencil.overflow.test.ts does), since the
		// transform itself is a false completion signal in "cap" mode: a
		// short string that already fits legitimately leaves no transform
		// set at all, indistinguishable from "not corrected yet" if a poll
		// read the transform instead.
		this.dataset.fitGen = String((Number(this.dataset.fitGen) || 0) + 1);
		if (!span) return;
		span.style.transform = "none";
		const naturalWidth = span.getBoundingClientRect().width;
		const targetWidth = this.getBoundingClientRect().width;
		if (naturalWidth <= 0 || targetWidth <= 0) return;
		const ratio =
			this.mode === "cap"
				? Math.min(1, targetWidth / naturalWidth)
				: targetWidth / naturalWidth;
		// A CORRECTED RUN IS ANCHORED TO THE BOX'S LEFT EDGE, NEVER CENTRED,
		// and a run that needs no correction is centred. Caught by a real
		// render, not assumed: `Hallen 07` on Apple's strip, whose shorter
		// allotment puts it on a tier where its natural render is NARROWER
		// than the content width, painted its last glyph past the strip's own
		// right edge. Alignment positions the span at its PRE-transform width
		// — a transform changes no layout — so a centred span that is then
		// scaled about its left edge lands half the width difference off the
		// box: to the right for a run scaled up, to the left for one scaled
		// down. Left-anchored, the scale runs from the box's own edge and the
		// painted run is exactly the box, in both modes and both directions.
		// At ratio 1 nothing is scaled, so nothing moves and the centring is
		// what a short brand name in "cap" mode is given the whole box for.
		this.style.textAlign = ratio === 1 ? "center" : "left";
		span.style.transform = `scaleX(${ratio})`;
	}

	/** `updated()` — Lit's own unconditionally-firing post-render hook, called
	 *  after every single reactive update regardless of whether ANY caller
	 *  ever reads `this.updateComplete` — is the correction's real trigger, not
	 *  the `getUpdateComplete` override below. `updateComplete` only resolves
	 *  when something EXTERNALLY awaits it (ticket-file.boot.ts's own
	 *  readiness contract explicitly does, for the PDF path), and nothing in
	 *  a live host page (the studio, the storefront, the catalog, the picker)
	 *  ever does — caught by a real render, not assumed: the second and
	 *  every later render of this element (a fresh event name on an
	 *  already-mounted card) landed with `transform: none` still on the span,
	 *  the exact state `#applyFit` starts from and never got past, because
	 *  nothing had called `getUpdateComplete` to run it. `updated()` fixes
	 *  that by construction — it always runs, so `#applyFit` always runs
	 *  with it, immediately with whatever font is already loaded. */
	override updated(changed: Map<PropertyKey, unknown>): void {
		super.updated(changed);
		this.#applyFit();
		void this.#applyFitOnceFontLoads();
	}

	/** The awaited half of the fix — belt and braces alongside `updated()`'s
	 *  own immediate, always-run correction above: this element's FIRST ever
	 *  render can still land before Anton has actually finished loading (a
	 *  real network/file fetch in every live-browser host, not the PDF
	 *  path's already-inline data URI), so `updated()`'s own immediate
	 *  `#applyFit` measures the fallback font's narrower or wider natural
	 *  width. Awaiting the exact family/weight this card's own `<style>`
	 *  block loads (ticket-inapp.stencil.template.ts's `.tkt-stencil-wordmark`)
	 *  and re-applying once it resolves is what corrects that first-render
	 *  case without depending on anything external ever reading
	 *  `updateComplete`. */
	async #applyFitOnceFontLoads(): Promise<void> {
		try {
			// Null in a host with no Font Loading API: nothing to await, and
			// the fit below still runs against whatever face is resolved.
			await ticketCardFontSet()?.load(
				`400 16px "${TICKET_CARD_DISPLAY_FAMILY}"`,
			);
		} catch {
			// A load() rejection (a font that fails to parse/fetch) still lets
			// the fit stand at whatever `updated()` already applied against the
			// fallback font — wrong-looking, never clipped.
		}
		this.#applyFit();
	}

	override async getUpdateComplete(): Promise<boolean> {
		const result = await super.getUpdateComplete();
		await this.#applyFitOnceFontLoads();
		return result;
	}
}
if (!customElements.get(FITTED_WORDMARK_TAG)) {
	customElements.define(FITTED_WORDMARK_TAG, TktFittedWordmark);
}

declare global {
	interface HTMLElementTagNameMap {
		[FITTED_WORDMARK_TAG]: TktFittedWordmark;
	}
}

/** The stencil card's own hero geometry, declared once here because two
 *  surfaces render it: the card itself (ticket-inapp.stencil.template.ts) and
 *  the wallet band (ticket-wallet.panel.ts), which is that same hero region at
 *  the band's own width. A second copy of these numbers beside the band is
 *  what lets a pass's typography drift from the card's. */
export const STENCIL_DESIGN_WIDTH_PX = 384;
export const STENCIL_GUTTER_PX = 24;
export const STENCIL_CONTENT_WIDTH_PX =
	STENCIL_DESIGN_WIDTH_PX - STENCIL_GUTTER_PX * 2;

/**
 * The hero wordmark's size ladder, longest name first. Font size governs how
 * BOLD the wordmark reads and never how WIDE — `renderWordmarkFullWidth`
 * forces the rendered width to the content width for any string — so a longer
 * title steps DOWN only to keep its own glyph compression legible. `boxPx` is
 * the line box that size needs; a slot with less room than the top tier's box
 * spends the tallest tier that fits rather than a size off this ladder. The top
 * rung's 14 is the length at and below which a name is taken in as one shape
 * rather than read as a line of text, which is what earns it the tallest
 * letterforms.
 */
export const STENCIL_WORDMARK_TIERS = [
	{ maxNameLength: Number.POSITIVE_INFINITY, fontSizePx: 50, boxPx: 53 },
	{ maxNameLength: 28, fontSizePx: 72, boxPx: 76 },
	{ maxNameLength: 14, fontSizePx: 104, boxPx: 109 },
] as const;

/** The tier this event name takes on a slot `availableBoxPx` tall — the card's
 *  own choice when the slot is the card's, and the tallest tier that fits when
 *  the slot is shorter (Apple's strip allotment is shorter than the top tier's
 *  own box). */
export function stencilWordmarkTier(
	eventName: string,
	availableBoxPx = Number.POSITIVE_INFINITY,
): { readonly fontSizePx: number; readonly boxPx: number } {
	const byLength = STENCIL_WORDMARK_TIERS.filter(
		(tier) => eventName.length <= tier.maxNameLength,
	);
	const fitting = byLength.filter((tier) => tier.boxPx <= availableBoxPx);
	// Falls back to the ladder's own smallest tier when even that overruns the
	// slot: the wordmark is width-fitted either way, so a slot too short for
	// every tier crops the line box rather than typesetting a size the card
	// has no counterpart for.
	return (
		fitting[fitting.length - 1] ??
		byLength[0] ?? {
			fontSizePx: STENCIL_WORDMARK_TIERS[0].fontSizePx,
			boxPx: STENCIL_WORDMARK_TIERS[0].boxPx,
		}
	);
}

/** The hero title, forced to span exactly `widthPx` for ANY
 *  string — `<tkt-fitted-wordmark mode="fill">` (this file, above) inside a
 *  wrapping box of that exact CSS width; the element fills it (`display:
 *  block`) and reads its own rendered width back as the fit target, so
 *  `widthPx` only ever needs stating once, here. `heightPx` sizes the
 *  wrapping box's other axis; the element's own measured-scale correction
 *  governs width alone. `overflow: hidden` here is a backstop only — the
 *  scale correction is what actually keeps content inside the box; nothing
 *  is expected to reach this edge. */
export function renderWordmarkFullWidth(
	text: string,
	{
		widthPx,
		heightPx,
		fontSizePx,
	}: { widthPx: number; heightPx: number; fontSizePx: number },
): TemplateResult {
	return html`
    <div
      style="width: ${widthPx}px; height: ${heightPx}px; overflow: hidden; display: flex; align-items: center;"
    >
      <tkt-fitted-wordmark
        text=${text}
        font-size-px=${fontSizePx}
        mode="fill"
      ></tkt-fitted-wordmark>
    </div>
  `;
}

/** The footer brand wordmark, capped to `maxWidthPx` — never
 *  clipped for an arbitrarily long or unbroken brand name, never stretched
 *  up to fill the box for a short one. `<tkt-fitted-wordmark mode="cap">`
 *  (this file, above); see `renderWordmarkFullWidth`'s own header
 *  for why `overflow: hidden` here is a backstop only. */
export function renderWordmarkFooter(
	text: string,
	{
		maxWidthPx,
		heightPx,
		fontSizePx,
	}: { maxWidthPx: number; heightPx: number; fontSizePx: number },
): TemplateResult {
	return html`
    <div
      style="width: ${maxWidthPx}px; height: ${heightPx}px; overflow: hidden; display: flex; align-items: center; justify-content: center;"
    >
      <tkt-fitted-wordmark
        text=${text}
        font-size-px=${fontSizePx}
        mode="cap"
      ></tkt-fitted-wordmark>
    </div>
  `;
}
